import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface LiveLocation {
  user_id: string;
  recorded_at: string;
  lat: number;
  lng: number;
  accuracy_m: number | null;
  battery_pct: number | null;
}

// Workers on an active shift who've gone location-silent (last fix > 15 min or
// none) — surfaced so the live map can flag them instead of silently freezing.
export interface TrackingGap {
  user_id: string;
  display_name: string;
  last_location_at: string | null;
  minutes_since: number;
}

export function useLiveLocations() {
  const api = useApi();
  return useQuery({
    queryKey: ['locations', 'live'],
    queryFn: () => api<{ locations: LiveLocation[]; gaps: TrackingGap[] }>('/locations/live'),
    refetchInterval: 30_000,
  });
}

interface BatchInput {
  points: Array<{
    recorded_at: string;
    lat: number;
    lng: number;
    accuracy_m?: number;
    battery_pct?: number;
    source: 'foreground' | 'background' | 'alarm' | 'check_in';
  }>;
}

export function usePostLocations() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BatchInput) =>
      api<{ accepted: number }>('/locations/batch', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  });
}
